"use client";

import * as React from "react";
import ReadMoreText from "@/components/ui/read-more-text";
import { cn } from "@/lib/utils";
import { useFixedWorkspaceControl } from "./use-fixed-workspace-control";

type WorkspaceControlProps = Omit<
  React.ComponentPropsWithRef<"div">,
  "title"
> & {
  title: React.ReactNode;
  /** 名前・説明・操作をまとめて置き換える。form を h1 の中に入れない。 */
  heading?: React.ReactNode;
  leftIcon?: React.ReactNode;
  actions?: React.ReactNode;
  description?: string;
  footer?: React.ReactNode;
};

/** ワークスペース内の主要なビューに共通する操作ヘッダー。 */
function WorkspaceControl({
  className,
  title,
  heading,
  leftIcon,
  actions,
  description,
  footer,
  ref,
  ...props
}: WorkspaceControlProps) {
  const controlRef = React.useRef<HTMLDivElement>(null);
  const { isFixed, placeholderHeight, fixedStyle } =
    useFixedWorkspaceControl(controlRef);

  return (
    <>
      {isFixed && (
        <div aria-hidden="true" style={{ height: placeholderHeight }} />
      )}
      <div
        ref={(element) => {
          controlRef.current = element;
          if (typeof ref === "function") return ref(element);
          if (ref) ref.current = element;
        }}
        data-workspace-control=""
        data-sticky={isFixed ? "" : undefined}
        style={isFixed ? fixedStyle : undefined}
        className={cn(
          "group/workspace-control bg-background z-30 flex flex-col @container @container/header",
          !isFixed &&
            "relative -mx-4 -mt-6 gap-4 px-4 pt-6 pb-4 md:-mx-6 md:px-6",
          className,
          isFixed && "fixed top-auto mt-0 gap-1 border-b px-4 py-4 md:px-6",
        )}
        {...props}
      >
        {heading ?? (
          <>
            <div className="flex flex-wrap-reverse items-center gap-x-4 gap-y-4 group-data-[sticky]/workspace-control:gap-y-1">
              <div className="flex w-max max-w-full shrink-0 items-center gap-2">
                {leftIcon && (
                  <div
                    className={cn("shrink-0 size-6", !isFixed && "@md:size-8")}
                  >
                    {leftIcon}
                  </div>
                )}
                {title && (
                  <h1
                    className={cn(
                      "font-bold text-foreground min-w-0 break-words line-clamp-2",
                      isFixed ? "text-lg" : "text-xl @md:text-2xl",
                    )}
                    title={typeof title === "string" ? title : undefined}
                  >
                    {title}
                  </h1>
                )}
              </div>
              {actions && (
                <div className="ml-auto shrink-0 print:hidden">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {actions}
                  </div>
                </div>
              )}
            </div>
            {description && !isFixed && <ReadMoreText text={description} />}
          </>
        )}
        {footer && (
          <div className="flex flex-wrap items-center gap-1">{footer}</div>
        )}
      </div>
    </>
  );
}

export { WorkspaceControl };
